import React, { useState, useContext } from 'react';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import { GlobalContext } from '../contexts/GlobalContext';
import Header from './shared/Header';
import Footer from './shared/Footer';
import QuestionCard from './form-comp/QuestionCard';
import NavigationButtons from './form-comp/NavigationButtons';
import SidebarQuestionList from './form-comp/SidebarQuestionList';
import { baseURL } from '../config/api'; 
import { questions } from './form-comp/questions';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const Forms = ({ onFinish }) => {
  const [responses, setResponses] = useState(Array(questions.length).fill(null));
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const {
    setScoreData,
    setQchatRespuestas,
    setResumenTotal,
    setEvolComunicativas,
    setEvolSociales,
    setPorcentajeComunicativas,
    setPorcentajeSociales,
    setAcumComunicativas,
    setAcumSociales,
    setHoraInicio,
    setHoraFin,
    setResultadoRiesgo,
    horaInicio
  } = useContext(GlobalContext);

  // ★ Validador central por tipo de pregunta
  const isValid = (idx) => {
    const q = questions[idx];
    const v = responses[idx];

    if (q.type === 'input') {
      if (v === null || v === '') return false;   // vacío no vale
      const n = Number(v);
      return Number.isFinite(n) && n >= 1 && n <= 18; // ajusta rango si quieres
    }
    // para select / default / yesno basta con que no sea null
    return v !== null;
  };

  const calculateScore = (resps) =>
    resps.slice(2, 12).reduce((acc, val, idx) => {
      if (val === null) return acc;
      if (idx < 9) return acc + (val >= 2 ? 1 : 0);
      if (idx === 9) return acc + (val <= 2 ? 1 : 0);
      return acc;
    }, 0);

  const calculateHabilidadPorcentajes = (resps) => {
    let sociales = 0;
    let comunicativas = 0;

    if (resps[4] >= 2) comunicativas += 20;
    if (resps[9] >= 2) comunicativas += 20;
    if (resps[10] >= 2) comunicativas += 20;
    if (resps[12] === 1) comunicativas += 25;
    if (resps[13] === 1) comunicativas += 15;

    if (resps[2] >= 2) sociales += 10;
    if (resps[3] >= 2) sociales += 10;
    if (resps[5] >= 2) sociales += 10;
    if (resps[6] >= 2) sociales += 15;
    if (resps[7] >= 2) sociales += 15;
    if (resps[8] >= 2) sociales += 15;
    if (resps[17] === 1) sociales += 20;
    if (resps[18] === 1) sociales += 5;

    return {
      habilidadesSociales: sociales,
      habilidadesComunicativas: comunicativas
    };
  };

  const formatearFechaLima = (fecha) => {
    return dayjs(fecha).tz("America/Lima").format();
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Si NO estoy en la última, no envío; navego a la última.
    if (currentQuestion !== questions.length - 1) {
      setCurrentQuestion(questions.length - 1);
      return;
    }

    // ★ seguridad: no enviar si hay alguna inválida
    if (questions.some((_, i) => !isValid(i))) return;

    const fin = new Date();
    setHoraFin(fin);

    const qchatScore = calculateScore(responses);
    const { habilidadesSociales, habilidadesComunicativas } = calculateHabilidadPorcentajes(responses);

    const qchatRespuestas = responses.slice(2, 12).map((val, idx) => {
      if (val === null) return 0;
      if (idx < 9) return val >= 2 ? 1 : 0;
      if (idx === 9) return val <= 2 ? 1 : 0;
      return 0;
    });

    let acumComunicativas = [];
    let acumSociales = [];
    let comTotal = 0;
    let socTotal = 0;

    if (responses[2] >= 2) socTotal += 10;
    acumSociales.push(socTotal);
    if (responses[3] >= 2) socTotal += 10;
    acumSociales.push(socTotal);
    if (responses[5] >= 2) socTotal += 10;
    acumSociales.push(socTotal);
    if (responses[6] >= 2) socTotal += 15;
    acumSociales.push(socTotal);
    if (responses[7] >= 2) socTotal += 15;
    acumSociales.push(socTotal);
    if (responses[8] >= 2) socTotal += 15;
    acumSociales.push(socTotal);
    if (responses[17] === 1) socTotal += 20;
    acumSociales.push(socTotal);
    if (responses[18] === 1) socTotal += 5;
    acumSociales.push(socTotal);

    if (responses[4] >= 2) comTotal += 20;
    acumComunicativas.push(comTotal);
    if (responses[9] >= 2) comTotal += 20;
    acumComunicativas.push(comTotal);
    if (responses[10] >= 2) comTotal += 20;
    acumComunicativas.push(comTotal);
    if (responses[12] === 1) comTotal += 25;
    acumComunicativas.push(comTotal);
    if (responses[13] === 1) comTotal += 15;
    acumComunicativas.push(comTotal);

    const edad = responses[0];
    const genero = responses[1] === 0 ? 1 : 0; // Masculino = 1, Femenino = 0
    const preguntasExtras = responses.slice(12);

    const evolSociales = [responses[2], responses[3], responses[5], responses[6], responses[7], responses[8], responses[17], responses[18]]
      .map((r, idx) => idx <= 5 ? (r >= 2 ? 1 : 0) : (r === 0 ? 1 : 0));

    const evolComunicativas = [responses[4], responses[9], responses[10], responses[12], responses[13]]
      .map((r, idx) => idx <= 2 ? (r >= 2 ? 1 : 0) : (r === 0 ? 1 : 0));

    const resumenTotal = [
      edad,
      genero,
      ...qchatRespuestas,
      qchatScore,
      ...preguntasExtras,
      socTotal,
      comTotal,
      formatearFechaLima(horaInicio),
      formatearFechaLima(fin)
    ];

    setScoreData(qchatRespuestas);
    setQchatRespuestas(qchatRespuestas);
    setResumenTotal(resumenTotal);
    setEvolComunicativas(evolComunicativas);
    setEvolSociales(evolSociales);
    setPorcentajeComunicativas(habilidadesComunicativas);
    setPorcentajeSociales(habilidadesSociales);
    setAcumComunicativas(acumComunicativas);
    setAcumSociales(acumSociales);

    localStorage.setItem('reportData', JSON.stringify({
      responses,
      questions,
      qchatRespuestas,
      acumComunicativas,
      acumSociales,
      porcentajeComunicativas: habilidadesComunicativas,
      porcentajeSociales: habilidadesSociales,
      horaInicio: formatearFechaLima(horaInicio),
      horaFin: formatearFechaLima(fin)
    }))

    fetch(`${baseURL}/predict`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ values: resumenTotal })
    })
      .then((res) => res.json())
      .then((data) => {
        const nuevaEntrada = [data.clase_predicha, data.riesgo_autismo];
        setResultadoRiesgo(prev => [...prev, nuevaEntrada]);

        const currentReportData = JSON.parse(localStorage.getItem('reportData')) || {};
        const prevResultados = currentReportData.resultadoRiesgo || [];
        currentReportData.resultadoRiesgo = [...prevResultados, nuevaEntrada];
        localStorage.setItem('reportData', JSON.stringify(currentReportData));
      })
      .catch((err) => { console.error("Error al enviar los datos:", err); });

    onFinish();
  };

  const handleChange = (qIndex, oIndex) => {
    const newResponses = [...responses];
    const question = questions[qIndex];

    if (question.type === "yesno") {
      newResponses[qIndex] = oIndex; 
    } else {
      if (qIndex === 0) {
        // edad: permite borrar → '' (invalidará el botón)
        if (typeof oIndex === 'number' && oIndex >= 0 && oIndex <= 18) {
          newResponses[qIndex] = oIndex;
        } else {
          newResponses[qIndex] = '';
        }
      } else {
        newResponses[qIndex] = oIndex;
      }
    }
    setResponses(newResponses);
  };

  const handleNext = (e) => {
  // Evita que el click se convierta en submit si el botón cambia de tipo en el mismo tick
    e?.preventDefault?.();

  // No avances si la actual no es válida
    if (!isValid(currentQuestion)) return;

    if (currentQuestion === 0) {
      const inicio = new Date();
      setHoraInicio(inicio);
    }

    if (currentQuestion < questions.length - 1) {
      // Diferir el avance para que el re-render no ocurra dentro del mismo click
      setTimeout(() => {
        setCurrentQuestion(prev => Math.min(prev + 1, questions.length - 1));
      }, 0);
    }
  };


  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const getVisibleQuestions = () => {
    const prevIndex = Math.max(0, currentQuestion - 4);
    const nextEndIndex = Math.min(questions.length, currentQuestion + 4);
    return questions.slice(prevIndex, nextEndIndex);
  };

  const isSelected = (qIndex, oIndex) => {
    const question = questions[qIndex];
    if (question.type === "yesno") {
      return responses[qIndex] === oIndex;
    }
    return responses[qIndex] === oIndex;
  };

  return (
    <div className="flex flex-col min-h-screen justify-between">
      <div className="max-w-7xl w-full mx-auto space-y-8 flex-grow">
        <Header
          title="Formulario de Evaluación"
          subtitle="Sistema de Evaluación de Autismo Infantil"
        />

        <div className="flex items-start">
          <div className="flex-1 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-lg p-8 w-full max-w-3xl">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-blue-800">Cuéntanos sobre tu hijo</h2>
                <span className="text-blue-600 font-semibold">
                  Pregunta {currentQuestion + 1} de {questions.length}
                </span>
              </div>

              <form
                onSubmit={handleSubmit}
                className="space-y-6"
                noValidate
                onKeyDown={(e) => {
                  // Evita que Enter envíe el formulario si NO estás en la última pregunta
                  if (e.key === 'Enter' && currentQuestion !== questions.length - 1) {
                    e.preventDefault();
                  }
                }}
              >
                <QuestionCard
                  question={questions[currentQuestion]}
                  response={responses[currentQuestion]}
                  onChange={(val) => handleChange(currentQuestion, val)}
                />

                <NavigationButtons
                  currentQuestion={currentQuestion}
                  totalQuestions={questions.length}
                  onPrevious={handlePrevious}
                  onNext={handleNext}
                  onSubmit={handleSubmit}   // (no es estrictamente necesario, el botón "Finalizar" ya es type="submit")
                  disabledNext={!isValid(currentQuestion)}
                  disabledSubmit={questions.some((_, i) => !isValid(i))}
                />
              </form>

            </div>
          </div>

          <SidebarQuestionList
            questions={getVisibleQuestions()}
            currentQuestion={currentQuestion}
            responses={responses}
          />
        </div>
      </div>
    </div>
  );
};

export default Forms;
